import React, { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import PlatformProductManagePage from '@/pages/ipe/workbench/PlatformProductManagePage';
import { ProductFilterCard, ProductListCard } from '@/tiktok-platform/modules/products/components/ProductPageCards';
import TiktokOpsListToolbar, { downloadCsv } from '@/tiktok-platform/components/TiktokOpsListToolbar';
import TiktokSyncProgressSlot from '@/tiktok-platform/components/TiktokSyncProgressSlot';
import TiktokProductSyncModal from '@/tiktok-platform/components/TiktokProductSyncModal';
import BatchQueryModal from '@/tiktok-platform/modules/products/components/BatchQueryModal';
import {
  BATCH_QUERY_LIMIT,
  joinBatchTokens,
  parseBatchTokens,
} from '@/tiktok-platform/modules/products/components/batchQueryUtils';
import { useTiktokPageShopScope, stackShopDimensionTabs } from '@/tiktok-platform/hooks/useTiktokPageShopScope';
import PlatformShopPickModal from '@/components/platform/PlatformShopPickModal';
import { isAllShopsScope } from '@/utils/platformShopScope';
import { useTiktokSyncJobs } from '@/tiktok-platform/context/TiktokSyncJobContext';
import { tiktokPlatformAPI } from '@/tiktok-platform/api/tiktokPlatformApi';
import { formatSiteListSyncedAt } from '@/tiktok-platform/utils/tiktokSiteList';
import { showTiktokConfirm } from '@/tiktok-platform/components/TiktokConfirmDialog';
import { fetchAllPages } from '@/tiktok-platform/utils/tiktokExportHelpers';
import { buildOpsExportMenu, opsExportLabel } from '@/tiktok-platform/utils/buildOpsExportMenu';
import { showToast } from '@/utils/toast';
import { readNoticeProductQuery } from '@/tiktok-platform/utils/tiktokNoticeFocusQuery';

const PRODUCT_EXPORT_HEADERS = [
  'id', 'title', 'platformSpuId', 'platformSkcId', 'platformSkuId',
  'publishStatus', 'priceReviewStatus', 'salePrice', 'supplyPrice',
  'stockQty', 'salesLast7Days', 'createdAt', 'publishedAt', 'listedAt', 'lastSyncedAt',
];

export { parseBatchTokens, BATCH_QUERY_LIMIT } from './batchQueryUtils';

const STATUS_TABS = [
  { id: 'all', labelKey: 'tiktokCatalog.all', statKey: 'total' },
  { id: 'listed', labelKey: 'tiktokCatalog.listed', statKey: 'listed' },
  { id: 'pending', labelKey: 'tiktokCatalog.pending', statKey: 'pending' },
  { id: 'sold_out', labelKey: 'tiktokCatalog.soldOut', statKey: 'soldOut' },
  { id: 'offline', labelKey: 'tiktokCatalog.offline', statKey: 'offline' },
  // 平台 DRAFT 商品不在此处设 Tab：草稿统一走侧栏「草稿箱」菜单。
];

function statusTabCount(stats, tab) {
  if (!stats) return 0;
  // 「全部」用后端去重 product_id 总数（TTS 无 SPU/SKC）
  if (tab.id === 'all') return Number(stats.total) || 0;
  return Number(stats[tab.statKey]) || 0;
}

const ID_TYPE_OPTIONS = [
  { value: 'productId', labelKey: 'tiktokCatalog.productId' },
  { value: 'sku', labelKey: 'tiktokCatalog.sku' },
];

const EMPTY_FILTERS = {
  idType: 'productId',
  idValue: '',
  supplierCode: '',
  brand: '',
};

const EMPTY_QUERY = {
  idType: 'productId',
  keywords: '',
  brand: '',
  supplierCode: '',
};

const BATCH_FIELDS = {
  idValue: {
    titleFor: (idType) => (idType === 'sku' ? 'tiktokCatalog.batchQuerySku' : 'tiktokCatalog.batchQueryProductId'),
    placeholderKey: 'tiktokCatalog.batchQueryCode',
  },
  supplierCode: {
    titleKey: 'tiktokCatalog.batchQuerySupplierTitle',
    placeholderKey: 'tiktokCatalog.batchQuerySupplierPh',
  },
  brand: {
    titleKey: 'tiktokCatalog.batchQueryBrandTitle',
    placeholderKey: 'tiktokCatalog.batchQueryBrandPh',
  },
};

/**
 * 产品目录面板
 */
export default function ProductCatalogPanel({
  mode = 'catalog',
  showStatusTabs = true,
}) {
  const { t } = useTranslation('workbench');
  const {
    activeShopId,
    activeShop,
    loading: shopLoading,
    shopDimensionTabs,
    concreteScopeId,
    allShopsScope,
    pageShopScope,
    scopedShops = [],
    isAllShopsMode,
    setPageShopScope,
  } = useTiktokPageShopScope();
  const { isBusy } = useTiktokSyncJobs();
  const [searchParams] = useSearchParams();
  const noticeFocusKey = searchParams.toString();
  const [filterDraft, setFilterDraft] = useState(EMPTY_FILTERS);
  const [query, setQuery] = useState(EMPTY_QUERY);
  const [, setQueryNonce] = useState(0);
  const [listingDimension, setListingDimension] = useState(
    mode === 'drafts' ? 'drafts' : mode === 'price_review' ? 'price_review' : mode === 'records' ? 'listed' : 'listed',
  );
  const [dimensionStats, setDimensionStats] = useState(null);
  const [selectedCount, setSelectedCount] = useState(0);
  const selectedIdsRef = useRef([]);
  const selectedSpuIdsRef = useRef([]);
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchModalField, setBatchModalField] = useState(null);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncModalConfirming, setSyncModalConfirming] = useState(false);
  const [shopPickOpen, setShopPickOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [persistedSyncedAt, setPersistedSyncedAt] = useState('');
  const [syncState, setSyncState] = useState({
    refreshing: false,
    listLoading: false,
    listTotal: 0,
    listPageCount: 0,
    cooldownSeconds: 0,
    refresh: null,
    lastRefreshedAt: '',
    exportSelected: null,
    exportPage: null,
    batchShelfOn: null,
    batchShelfOff: null,
    clearSelection: null,
  });

  // 单店 API：页内选「全部」时需先选具体店；具体店用 concreteScopeId
  const listShopId = concreteScopeId;
  const listReady = Boolean(listShopId);

  const productSyncBusy = isBusy('product', listShopId || activeShopId);

  useEffect(() => {
    const notice = readNoticeProductQuery(searchParams);
    const nextDim = mode === 'drafts'
      ? 'drafts'
      : mode === 'price_review'
        ? 'price_review'
        : mode === 'records'
          ? 'listed'
          : (notice?.dim || 'listed');
    setListingDimension(nextDim);
    if (notice?.keywords) {
      setFilterDraft({
        ...EMPTY_FILTERS,
        idType: notice.idType === 'sku' ? 'sku' : 'productId',
        idValue: notice.keywords,
      });
      setQueryNonce((n) => {
        const next = n + 1;
        setQuery({
          idType: notice.idType === 'sku' ? 'sku' : 'productId',
          keywords: notice.keywords,
          brand: '',
          supplierCode: '',
          _nonce: next,
        });
        return next;
      });
    } else {
      setFilterDraft(EMPTY_FILTERS);
      setQueryNonce((n) => {
        const next = n + 1;
        setQuery({ ...EMPTY_QUERY, _nonce: next });
        return next;
      });
    }
    setSelectedCount(0);
    selectedIdsRef.current = [];
    selectedSpuIdsRef.current = [];
    setBatchModalField(null);
    setPersistedSyncedAt('');
  }, [mode, listShopId, noticeFocusKey, searchParams]);

  useEffect(() => {
    if (!listShopId) {
      setPersistedSyncedAt('');
      return undefined;
    }
    let cancelled = false;
    tiktokPlatformAPI.getShopSyncStatus(listShopId)
      .then((res) => {
        if (cancelled) return;
        const modules = Array.isArray(res?.modules) ? res.modules : [];
        const product = modules.find((m) => String(m.module || '').toUpperCase() === 'PRODUCT');
        const at = product?.lastSyncEndAt || product?.lastRunAt || '';
        setPersistedSyncedAt(at ? String(at) : '');
      })
      .catch(() => {
        if (!cancelled) setPersistedSyncedAt('');
      });
    return () => { cancelled = true; };
  }, [listShopId]);

  const latestSyncedAt = syncState.lastRefreshedAt || persistedSyncedAt;
  const latestSyncedLabel = latestSyncedAt
    ? t('tiktokCatalog.updatedAt', { time: formatSiteListSyncedAt(latestSyncedAt) })
    : t('tiktokCatalog.notSynced');

  const onQuery = () => {
    const idTokens = parseBatchTokens(filterDraft.idValue).slice(0, BATCH_QUERY_LIMIT);
    const supplierTokens = parseBatchTokens(filterDraft.supplierCode).slice(0, BATCH_QUERY_LIMIT);
    const brandTokens = parseBatchTokens(filterDraft.brand).slice(0, BATCH_QUERY_LIMIT);
    setSyncState((prev) => ({ ...prev, listLoading: true }));
    setQueryNonce((n) => {
      const next = n + 1;
      setQuery({
        idType: filterDraft.idType || 'productId',
        keywords: idTokens.join(','),
        brand: brandTokens.join(','),
        supplierCode: supplierTokens.join(','),
        _nonce: next,
      });
      return next;
    });
  };

  const onReset = () => {
    setFilterDraft(EMPTY_FILTERS);
    setQueryNonce((n) => {
      const next = n + 1;
      setQuery({ ...EMPTY_QUERY, _nonce: next });
      return next;
    });
    if (mode === 'catalog') setListingDimension('listed');
  };

  const updateField = (key, value) => {
    setFilterDraft((prev) => ({ ...prev, [key]: value }));
  };

  const openBatchModal = (fieldKey) => {
    setBatchModalField(fieldKey);
  };

  const onBatchConfirm = (tokens) => {
    if (!batchModalField) return;
    updateField(batchModalField, joinBatchTokens(tokens));
  };

  const batchModalMeta = batchModalField ? BATCH_FIELDS[batchModalField] : null;
  const batchModalTitle = batchModalMeta
    ? t(batchModalMeta.titleFor?.(filterDraft.idType) || batchModalMeta.titleKey || 'tiktokCatalog.batchQuery')
    : t('tiktokCatalog.batchQuery');

  // 仅同步展示态；回调走 ref，避免父组件因函数引用变化反复重渲染
  const handleSyncState = useCallback((state) => {
    setSyncState((prev) => {
      const nextLast = state?.lastRefreshedAt || prev.lastRefreshedAt || '';
      const next = {
        ...prev,
        lastRefreshedAt: nextLast,
        refreshing: Boolean(state?.refreshing),
        listLoading: Boolean(state?.listLoading),
        listTotal: Number.isFinite(Number(state?.listTotal)) ? Number(state.listTotal) : prev.listTotal,
        listPageCount: Number.isFinite(Number(state?.listPageCount)) ? Number(state.listPageCount) : prev.listPageCount,
        cooldownSeconds: state?.cooldownSeconds || 0,
        refresh: state?.refresh ?? prev.refresh,
        reloadList: state?.reloadList ?? prev.reloadList,
        exportSelected: state?.exportSelected ?? prev.exportSelected,
        exportPage: state?.exportPage ?? prev.exportPage,
        batchShelfOn: state?.batchShelfOn ?? prev.batchShelfOn,
        batchShelfOff: state?.batchShelfOff ?? prev.batchShelfOff,
        clearSelection: state?.clearSelection ?? prev.clearSelection,
      };
      if (
        next.lastRefreshedAt === prev.lastRefreshedAt
        && next.refreshing === prev.refreshing
        && next.listLoading === prev.listLoading
        && next.listTotal === prev.listTotal
        && next.listPageCount === prev.listPageCount
        && next.cooldownSeconds === prev.cooldownSeconds
        && next.refresh === prev.refresh
        && next.reloadList === prev.reloadList
        && next.exportSelected === prev.exportSelected
        && next.exportPage === prev.exportPage
        && next.batchShelfOn === prev.batchShelfOn
        && next.batchShelfOff === prev.batchShelfOff
        && next.clearSelection === prev.clearSelection
      ) {
        return prev;
      }
      return next;
    });
    if (state?.lastRefreshedAt) {
      setPersistedSyncedAt(String(state.lastRefreshedAt));
    }
  }, []);

  const handleSelectionChange = useCallback((payload) => {
    if (Array.isArray(payload)) {
      selectedIdsRef.current = payload;
      selectedSpuIdsRef.current = [];
      setSelectedCount(payload.length);
      return;
    }
    const ids = Array.isArray(payload?.ids) ? payload.ids : [];
    const spuIds = Array.isArray(payload?.spuIds) ? payload.spuIds : [];
    selectedIdsRef.current = ids;
    selectedSpuIdsRef.current = spuIds;
    const spuCount = Number(payload?.count);
    setSelectedCount(Number.isFinite(spuCount) && spuCount >= 0 ? spuCount : spuIds.length);
  }, []);

  const buildProductListParams = useCallback(() => ({
    shopId: listShopId || undefined,
    priceReviewStatus: 'all',
    publishStatus: 'all',
    listingDimension: mode === 'catalog' ? listingDimension : undefined,
    idType: query?.idType || undefined,
    keywords: query?.keywords || undefined,
    brand: query?.brand || undefined,
    supplierCode: query?.supplierCode || undefined,
  }), [listShopId, listingDimension, mode, query]);

  const writeProductsCsv = useCallback((rows, filename) => {
    downloadCsv(
      filename,
      PRODUCT_EXPORT_HEADERS,
      rows.map((row) => ({
        id: row.id,
        title: row.title,
        platformSpuId: row.platformSpuId,
        platformSkcId: row.platformSkcId,
        platformSkuId: row.platformSkuId,
        publishStatus: row.publishStatus,
        priceReviewStatus: row.priceReviewStatus,
        salePrice: row.salePrice,
        supplyPrice: row.supplyPrice,
        stockQty: row.stockQty,
        salesLast7Days: row.salesLast7Days,
        createdAt: row.createdAt,
        publishedAt: row.publishedAt,
        listedAt: row.listedAt,
        lastSyncedAt: row.lastSyncedAt,
      })),
    );
  }, []);

  const fetchAllProducts = useCallback(async () => {
    const base = buildProductListParams();
    return fetchAllPages(async (page, pageSize) => {
      const res = await tiktokPlatformAPI.listProducts({ ...base, page, pageSize });
      return {
        items: Array.isArray(res?.items) ? res.items : [],
        total: Number(res?.total) || 0,
      };
    });
  }, [buildProductListParams]);

  const exportFilteredProducts = useCallback(async () => {
    if (!listReady) {
      showToast(allShopsScope ? t('tiktokCatalog.pickConcreteShop') : t('tiktokCatalog.pickShop'), 'warning');
      return;
    }
    if (exporting) return;
    setExporting(true);
    try {
      const rows = await fetchAllProducts();
      if (!rows.length) {
        showToast(t('tiktokCatalog.nothingToExport'), 'warning');
        return;
      }
      writeProductsCsv(rows, `tiktok-products-filtered-${Date.now()}.csv`);
      showToast(t('tiktokCatalog.exported', { count: rows.length }), 'success');
    } catch (e) {
      showToast(e?.message || t('tiktokCatalog.exportFilterFailed'), 'error');
    } finally {
      setExporting(false);
    }
  }, [listReady, allShopsScope, exporting, fetchAllProducts, writeProductsCsv, t]);

  const exportPageProducts = useCallback(() => {
    if (typeof syncState.exportPage === 'function') {
      syncState.exportPage();
      return;
    }
    showToast(t('tiktokCatalog.pageEmptyExport'), 'warning');
  }, [syncState.exportPage, t]);

  const exportSelectedProducts = useCallback(() => {
    if (typeof syncState.exportSelected === 'function') {
      syncState.exportSelected();
      return;
    }
    showToast(t('tiktokCatalog.pickExport'), 'warning');
  }, [syncState.exportSelected, t]);

  const listTotal = Number(syncState.listTotal) || 0;
  const listPageCount = Number(syncState.listPageCount) || 0;

  const selectedProductIds = useCallback(() => (
    (selectedSpuIdsRef.current || [])
      .map((s) => String(s || '').trim())
      .filter((s) => s && s !== '—')
  ), []);

  const afterSelectionMutation = useCallback(async () => {
    selectedIdsRef.current = [];
    selectedSpuIdsRef.current = [];
    setSelectedCount(0);
    syncState.clearSelection?.();
    setSyncState((prev) => ({ ...prev, listLoading: true }));
    try {
      await (syncState.reloadList?.() ?? Promise.resolve());
    } finally {
      setSyncState((prev) => ({ ...prev, listLoading: false }));
    }
  }, [syncState.clearSelection, syncState.reloadList]);

  const toastPlatformBatchResult = useCallback((res, label) => {
    const ok = Number(res?.successCount ?? 0);
    const fail = Number(res?.failureCount ?? 0);
    const failures = Array.isArray(res?.failures) ? res.failures : [];
    const firstFail = failures[0];
    const failHint = firstFail
      ? t('tiktokCatalog.failHint', {
        identity: firstFail.identity || '',
        msg: firstFail.msg ? `: ${firstFail.msg}` : '',
      })
      : '';
    if (ok > 0 && fail <= 0) {
      showToast(t('tiktokCatalog.batchDone', { label, count: ok }), 'success');
    } else if (ok > 0 && fail > 0) {
      showToast(t('tiktokCatalog.batchPartial', { label, ok, fail, hint: failHint }), 'warning');
    } else {
      showToast(fail > 0
        ? t('tiktokCatalog.batchFailed', { label, count: fail, hint: failHint })
        : t('tiktokCatalog.batchNone', { label }), 'error');
    }
  }, [t]);

  const onBatchDeleteLocal = async () => {
    const spuIds = selectedProductIds();
    if (!listShopId || !spuIds.length) {
      showToast(t('tiktokCatalog.pickDeleteLocal'), 'warning');
      return;
    }
    const ok = await showTiktokConfirm({
      title: t('tiktokCatalog.deleteLocalTitle'),
      message: t('tiktokCatalog.deleteLocalMessage', { count: spuIds.length }),
      tone: 'danger',
      confirmText: t('tiktokCatalog.deleteLocal'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setBatchBusy(true);
    try {
      const res = await tiktokPlatformAPI.batchDeleteProducts(listShopId, { spuNames: spuIds });
      const deletedSpus = Number(res?.deletedSpus);
      const deleted = Number(res?.deleted);
      if (Number.isFinite(deletedSpus) && deletedSpus > 0) {
        showToast(t('tiktokCatalog.deletedSpuCache', { count: deletedSpus }), 'success');
      } else if (Number.isFinite(deleted) && deleted > 0) {
        showToast(t('tiktokCatalog.deletedRowCache', { count: deleted }), 'success');
      } else {
        showToast(t('tiktokCatalog.deletedNone'), 'warning');
      }
      await afterSelectionMutation();
    } catch (e) {
      showToast(e?.message || t('tiktokCatalog.deleteLocalFailed'), 'error');
    } finally {
      setBatchBusy(false);
    }
  };

  const onBatchDeletePlatform = async () => {
    const spuIds = selectedProductIds();
    if (!listShopId || !spuIds.length) {
      showToast(t('tiktokCatalog.pickDeletePlatform'), 'warning');
      return;
    }
    const ok = await showTiktokConfirm({
      title: t('tiktokCatalog.deletePlatformTitle'),
      message: t('tiktokCatalog.deletePlatformMessage', { count: spuIds.length }),
      tone: 'danger',
      confirmText: t('tiktokCatalog.deletePlatform'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setBatchBusy(true);
    try {
      const res = await tiktokPlatformAPI.deleteProductsOnPlatform(listShopId, { spuNames: spuIds });
      toastPlatformBatchResult(res, t('tiktokCatalog.deletePlatform'));
      await afterSelectionMutation();
    } catch (e) {
      showToast(e?.message || t('tiktokCatalog.deletePlatformFailed'), 'error');
    } finally {
      setBatchBusy(false);
    }
  };

  const onBatchRecover = async () => {
    const spuIds = selectedProductIds();
    if (!listShopId || !spuIds.length) {
      showToast(t('tiktokCatalog.pickRecover'), 'warning');
      return;
    }
    const ok = await showTiktokConfirm({
      title: t('tiktokCatalog.recoverTitle'),
      message: t('tiktokCatalog.recoverMessage', { count: spuIds.length }),
      tone: 'warning',
      confirmText: t('tiktokCatalog.recover'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setBatchBusy(true);
    try {
      const res = await tiktokPlatformAPI.recoverProductsOnPlatform(listShopId, { spuNames: spuIds });
      toastPlatformBatchResult(res, t('tiktokCatalog.recover'));
      await afterSelectionMutation();
    } catch (e) {
      showToast(e?.message || t('tiktokCatalog.recoverFailed'), 'error');
    } finally {
      setBatchBusy(false);
    }
  };

  const statusTabs = useMemo(() => {
    if (!showStatusTabs || mode !== 'catalog') return null;
    return (
      <div className="tko-status-tabs" role="tablist" aria-label={t('tiktokCatalog.statusAria')}>
        {STATUS_TABS.map((tab) => {
          const count = statusTabCount(dimensionStats, tab);
          const active = listingDimension === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              className={`tko-status-tab${active ? ' tko-status-tab--active' : ''}`}
              onClick={() => {
                startTransition(() => setListingDimension(tab.id));
              }}
            >
              {t(tab.labelKey)}
              <span className="tko-status-tab__count">{count}</span>
            </button>
          );
        })}
      </div>
    );
  }, [showStatusTabs, mode, dimensionStats, listingDimension, t]);

  const tabs = useMemo(
    () => stackShopDimensionTabs(shopDimensionTabs, statusTabs),
    [shopDimensionTabs, statusTabs],
  );

  const idTypeLabel = t(ID_TYPE_OPTIONS.find((o) => o.value === filterDraft.idType)?.labelKey || 'tiktokCatalog.productId');

  return (
    <>
      <TiktokSyncProgressSlot kinds={['product']} />
      <ProductFilterCard
        toolbar={(
          <TiktokOpsListToolbar
            selectedCount={selectedCount}
            busy={batchBusy || exporting}
            syncKinds={['product']}
            onSync={() => {
              if (!listReady && !allShopsScope && !isAllShopsMode) {
                showToast(t('tiktokCatalog.pickAuthorized'), 'warning');
                return;
              }
              if (productSyncBusy) {
                showToast(t('tiktokCatalog.syncBusy'), 'warning');
                return;
              }
              if (allShopsScope || isAllShopsMode || isAllShopsScope(pageShopScope)) {
                setShopPickOpen(true);
                return;
              }
              setSyncModalOpen(true);
            }}
            syncing={syncState.refreshing || productSyncBusy}
            syncCooldownSeconds={syncState.cooldownSeconds}
            syncDisabled={shopLoading || productSyncBusy || (!(listReady || allShopsScope || isAllShopsMode)) || (listReady && !syncState.refresh)}
            lastSyncedLabel={latestSyncedLabel}
            syncHint={t('tiktokCatalog.syncHint')}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!listReady || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: listTotal,
              pageCount: listPageCount,
              selectedCount: selectedCount,
              onExportFiltered: exportFilteredProducts,
              onExportPage: exportPageProducts,
              onExportSelected: exportSelectedProducts,
            })}
            extraActions={[
              {
                key: 'batch-lifecycle',
                label: selectedCount > 0 ? t('tiktokCatalog.batchCount', { count: selectedCount }) : t('tiktokCatalog.batch'),
                disabled: selectedCount === 0 || batchBusy,
                menu: [
                  {
                    key: 'activate',
                    label: t('tiktokCatalog.activate'),
                    count: selectedCount,
                    disabled: selectedCount === 0 || batchBusy,
                    onClick: () => {
                      if (!selectedCount) {
                        showToast(t('tiktokCatalog.pickActivate'), 'warning');
                        return;
                      }
                      if (typeof syncState.batchShelfOn === 'function') {
                        syncState.batchShelfOn();
                        return;
                      }
                      showToast(t('tiktokCatalog.activateNotReady'), 'warning');
                    },
                  },
                  {
                    key: 'deactivate',
                    label: t('tiktokCatalog.deactivate'),
                    count: selectedCount,
                    disabled: selectedCount === 0 || batchBusy,
                    onClick: () => {
                      if (!selectedCount) {
                        showToast(t('tiktokCatalog.pickDeactivate'), 'warning');
                        return;
                      }
                      if (typeof syncState.batchShelfOff === 'function') {
                        syncState.batchShelfOff();
                        return;
                      }
                      showToast(t('tiktokCatalog.deactivateNotReady'), 'warning');
                    },
                  },
                  {
                    key: 'recover',
                    label: t('tiktokCatalog.recover'),
                    count: selectedCount,
                    disabled: selectedCount === 0 || batchBusy || !listReady,
                    onClick: () => {
                      if (!selectedCount) {
                        showToast(t('tiktokCatalog.pickRecover'), 'warning');
                        return;
                      }
                      onBatchRecover();
                    },
                  },
                  {
                    key: 'delete-local',
                    label: t('tiktokCatalog.deleteLocalTitle'),
                    count: selectedCount,
                    disabled: selectedCount === 0 || batchBusy || !listReady,
                    onClick: () => {
                      if (!selectedCount) {
                        showToast(t('tiktokCatalog.pickDelete'), 'warning');
                        return;
                      }
                      onBatchDeleteLocal();
                    },
                  },
                  {
                    key: 'delete-platform',
                    label: t('tiktokCatalog.deletePlatformTitle'),
                    count: selectedCount,
                    disabled: selectedCount === 0 || batchBusy || !listReady,
                    onClick: () => {
                      if (!selectedCount) {
                        showToast(t('tiktokCatalog.pickDelete'), 'warning');
                        return;
                      }
                      onBatchDeletePlatform();
                    },
                  },
                ],
              },
            ]}
          />
        )}
      >
        <div className="tko-filter-bar">
          <div className="tko-filter-bar__fields">
            <div className="tko-field tko-field--inline tko-field--id-combo tko-field--batch">
              <select
                className="tko-select tko-select--compact"
                value={filterDraft.idType}
                onChange={(e) => updateField('idType', e.target.value)}
                aria-label={t('tiktokCatalog.idTypeAria')}
              >
                {ID_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                ))}
              </select>
              <input
                className="tko-input"
                value={filterDraft.idValue}
                placeholder={t('tiktokCatalog.idPlaceholder', { label: idTypeLabel })}
                onChange={(e) => updateField('idValue', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="tko-batch-add-btn"
                aria-label={t('tiktokCatalog.batchInput', { label: idTypeLabel })}
                title={t('tiktokCatalog.batchQuery')}
                onClick={() => openBatchModal('idValue')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>

            <div className="tko-field tko-field--inline tko-field--batch">
              <span className="tko-field__label">{t('tiktokCatalog.supplier')}</span>
              <input
                className="tko-input"
                value={filterDraft.supplierCode}
                placeholder={t('tiktokCatalog.supplierPlaceholder')}
                onChange={(e) => updateField('supplierCode', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="tko-batch-add-btn"
                aria-label={t('tiktokCatalog.batchInputSupplier')}
                title={t('tiktokCatalog.batchQuery')}
                onClick={() => openBatchModal('supplierCode')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>

            <div className="tko-field tko-field--inline tko-field--batch">
              <span className="tko-field__label">{t('tiktokCatalog.brand')}</span>
              <input
                className="tko-input"
                value={filterDraft.brand}
                placeholder={t('tiktokCatalog.brandPlaceholder')}
                onChange={(e) => updateField('brand', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="tko-batch-add-btn"
                aria-label={t('tiktokCatalog.batchInputBrand')}
                title={t('tiktokCatalog.batchQuery')}
                onClick={() => openBatchModal('brand')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="tko-filter-bar__actions">
            <button type="button" className="tko-btn tko-btn--secondary tko-btn--sm" onClick={onReset}>
              {t('tiktokCatalog.reset')}
            </button>
            <button type="button" className="tko-btn tko-btn--primary tko-btn--sm" onClick={onQuery}>
              {t('tiktokCatalog.search')}
            </button>
          </div>
        </div>
      </ProductFilterCard>

      {productSyncBusy ? (
        <p className="tko-product-sync-live-hint" role="status">
          {t('tiktokCatalog.syncLive')}
        </p>
      ) : null}
      <ProductListCard
        tabs={tabs}
        // 仅列表查询时遮罩；同步中由进度条展示，勿盖「正在查询商品…」
        loading={Boolean(listReady) && syncState.listLoading}
        loadingLabel={t('tiktokCatalog.loadingProducts')}
      >
        <PlatformProductManagePage
          showToast={showToast}
          embedded
          workspace="tiktok"
          forcedShopId={listShopId || ''}
          tiktokExternalToolbar
          opsExternalChrome
          enableTiktokOpsToolbar={false}
          hideDimensionTabs
          lockListingDimension={mode !== 'catalog'}
          initialListingDimension={listingDimension}
          controlledListingDimension={listingDimension}
          onListingDimensionChange={setListingDimension}
          controlledQuery={query}
          onDimensionStatsChange={setDimensionStats}
          onSelectionChange={handleSelectionChange}
          onTiktokSyncStateChange={handleSyncState}
        />
      </ProductListCard>

      <BatchQueryModal
        open={Boolean(batchModalField)}
        title={batchModalTitle}
        initialValue={batchModalField ? filterDraft[batchModalField] : ''}
        placeholder={batchModalMeta ? t(batchModalMeta.placeholderKey) : ''}
        onConfirm={onBatchConfirm}
        onClose={() => setBatchModalField(null)}
      />
      <TiktokProductSyncModal
        open={syncModalOpen}
        confirming={syncModalConfirming}
        onClose={() => {
          if (!syncModalConfirming) setSyncModalOpen(false);
        }}
        onConfirm={async (payload) => {
          if (!syncState.refresh) {
            showToast(t('tiktokCatalog.syncNotReady'), 'warning');
            return;
          }
          setSyncModalConfirming(true);
          try {
            const started = await syncState.refresh(payload);
            if (started !== false) {
              setSyncModalOpen(false);
            }
          } catch (e) {
            showToast(e?.message || t('tiktokCatalog.syncStartFailed'), 'error');
          } finally {
            setSyncModalConfirming(false);
          }
        }}
      />
      <PlatformShopPickModal
        open={shopPickOpen}
        shops={scopedShops}
        title={t('tiktokCatalog.pickSyncShop')}
        description={t('tiktokCatalog.pickSyncShopDesc')}
        confirmText={t('tiktokCatalog.continueSync')}
        initialShopId={concreteScopeId || ''}
        onClose={() => setShopPickOpen(false)}
        onConfirm={(shopId) => {
          setShopPickOpen(false);
          setPageShopScope(String(shopId));
          setSyncModalOpen(true);
        }}
      />
    </>
  );
}
